import React from 'react';
import {View, Image, Platform, Linking} from 'react-native';

import { StyleSheet } from 'react-native';
import {colorPalette} from '../../../core/config/color.config';
import {useNavigation} from '@react-navigation/native';

import {RNEText, RNEButton, SVGIcon} from '../../components';
import {formatAmount, getConsultationFeeWithService} from '../../utils/currency.utils';
import ContentScrollView from '../scroll/ContentScrollView';
import {NavigationKey} from '../../navigations/config';
import { width, totalSize } from 'react-native-dimension';

import {useGetPractitionerProfile} from './../../server/react-query/usePractitioner';

interface PractitionerProfileViewModalProps {
  practitionerId: string;
  onClose?: () => void;
}

const PractitionerProfileViewModal = (
  props: PractitionerProfileViewModalProps,
) => {
  const navigation = useNavigation();

  const {data, isLoading} = useGetPractitionerProfile({
    practitionerId: props.practitionerId,
  });

  const handleSendChat = () => {
    if (Platform.OS === 'web') {
      props.onClose?.();
      navigation.navigate(NavigationKey.ADMIN_CHATS as never, {
        practitionerId: props.practitionerId,
      } as never);
    }
  };

  const handleSendMail = () => {
    if (Platform.OS === 'web' && data?.email) {
      const email = data.email;
      Linking.openURL(`mailto:${email}`);
    }
  };

  return (
    <View style={styles.root}>
      <View style={styles.headerContainer}>
        <View style={styles.mainDetailContainer}>
          <View style={styles.mainDetailItem}>
            {!isLoading ? (
              <RNEText
                label={`Dr. ${data?.humanName.given[0]} ${data?.humanName.family}`}
                variant="title"
                weight="600"
                color="blackLight"
                numberOfLines={2}
                ellipsizeMode="tail"
              />
            ) : (
              <RNEButton
                height={24}
                loading={isLoading}
                type="clear"
                loadingProps={loadingStyle}
                buttonStyle={buttonStyle}
              />
            )}
          </View>

          <View style={styles.mainDetailItem}>
            <RNEText
              label={
                data && data.practitionerRole[0]
                  ? data?.organization[0].address?.city
                    ? `${data?.practitionerRole[0].specialty.text} at ${data?.organization[0].address?.city}`
                    : `${data?.practitionerRole[0].specialty.text}`
                  : '--'
              }
              color="blackLight"
            />
          </View>

          <View style={styles.mainDetailItem}>
            <View style={styles.mainDetailItemKey}>
              <RNEText label={'License Number:'} />
            </View>
            <View style={styles.mainDetailItemValue}>
              <RNEText
                label={
                  data && data.qualification
                    ? `${data.qualification.identifier}`
                    : '--'
                }
              />
            </View>
          </View>

          <View style={styles.mainDetailItem}>
            <View style={styles.mainDetailItemKey}>
              <RNEText label={'Consultation Fee:'} />
            </View>
            <View style={styles.mainDetailItemValue}>
              <RNEText
                label={
                  data && data.fee
                    ? `${data.fee.amount.currency} ${formatAmount(
                        getConsultationFeeWithService(data?.fee.amount.value),
                      )}`
                    : '--'
                }
                weight="bold"
              />
            </View>
          </View>
        </View>
        <View style={styles.profilePictureContainer}>
          {data && data.photo ? (
            <Image
              source={{
                uri: data.photo.url,
                width: 96,
                height: 96,
              }}
              style={imageStyle}
              accessibilityLabel={data.photo.title}
            />
          ) : (
            <SVGIcon name="user" size={48} />
          )}
        </View>
      </View>

      <View style={styles.otherContentContainer}>
        <View style={styles.otherContentContainerHeader}>
          <RNEText
            label={'What cases I usually handle:'}
            color="accent"
            weight="600"
          />
        </View>

        <ContentScrollView isWeb>
          <RNEText label={data?.handledCases || '--'} />
        </ContentScrollView>
      </View>

      {Platform.OS === 'web' ? (
        <View style={styles.actionContainer}>
          <RNEButton
            title="SEND CHAT"
            color="warning"
            variant="default"
            onPress={handleSendChat}
            icon={<SVGIcon name="chat-white-icon" size={totalSize(3)} width={totalSize(2)} height={totalSize(3)} />}
            iconPosition="left"
            style={{...styles.buttonStyle}}
          />
          <RNEButton
            title="SEND MAIL"
            color="warning"
            variant="default"
            onPress={handleSendMail}
            icon={<SVGIcon name="send-email-solid"  size={totalSize(3)} width={totalSize(2)} height={totalSize(3)} />}
            iconPosition="left"
            style={{...styles.buttonStyle, gap:totalSize(0.3)}}
          />
          <RNEButton
            title="BACK"
            color="warning"
            variant="outlined"
            onPress={props.onClose}
            style={styles.buttonStyle}
          />
        </View>
      ) : (
        <View style={styles.actionContainer}>
          <RNEButton
            title="Back"
            color="secondary"
            variant="outlined"
            onPress={props.onClose}
            style={styles.buttonStyle}
          />
        </View>
      )}
    </View>
  );
};

const buttonStyle = {
  paddingHorizontal: 0,
};

const loadingStyle = {
  color: colorPalette.blackLight,
};

const imageStyle = {
  width: 96,
  height: 96,
  borderRadius: 96,
};

const styles = StyleSheet.create( ({
  root: {
    width: 720,
    paddingHorizontal: 48,
    paddingVertical: 32,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1.5,
    borderBottomColor: 'rgba(229, 229, 229, 0.8)',
    paddingBottom: 32,
  },
  mainDetailContainer: {
    flex: 1,
  },
  mainDetailItem: {
    marginBottom: 4,
    flexDirection: 'row',
  },
  mainDetailItemKey: {
    marginRight: 4,
  },
  mainDetailItemValue: {},
  profilePictureContainer: {
    width: 96,
    height: 96,
    borderRadius: 96,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colorPalette.light,
    marginLeft: 24,
  },
  otherContentContainer: {
    marginTop: 48,
    maxHeight: 200,
  },
  otherContentContainerHeader: {
    marginBottom: 4,
  },
  actionContainer: {
    marginTop: 48,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  buttonStyle: {
    width: width(12),
    height: totalSize(2.5),
    marginHorizontal: 8,
    borderWidth:totalSize(0.1),
    gap:totalSize(0.6),
  },
}));

export default PractitionerProfileViewModal;
